import React, { useState } from 'react';
import { contactData } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send, Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setIsSubmitted(true);
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
      // Reset success state after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    }
  };

  return (
    <section id="contact" className="py-24 bg-purple-100/60 relative overflow-hidden">
      {/* Background Decorations - contained within section */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-[#7ed321]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#6b2d7b]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#6b2d7b] mb-4">
            {contactData.title}
          </h2>
          <p className="text-xl text-slate-600 mb-4">
            {contactData.subtitle}
          </p>
          <p className="text-lg text-[#6b2d7b] font-medium">
            {contactData.cta}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-[#6b2d7b] mb-6">
                Get in Touch
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Whether you're looking to optimize operations, train your team, or explore strategic partnerships, we're here to help you succeed.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <a
                href={`mailto:${contactData.email}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-[#6b2d7b]/10 transition-colors group"
              >
                <div className="w-12 h-12 rounded-lg bg-[#7ed321]/20 flex items-center justify-center group-hover:bg-[#7ed321] transition-colors">
                  <Mail className="w-5 h-5 text-[#6b2d7b] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="text-[#6b2d7b] font-medium">{contactData.email}</p>
                </div>
              </a>

              <a
                href={`tel:${contactData.phone}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-[#6b2d7b]/10 transition-colors group"
              >
                <div className="w-12 h-12 rounded-lg bg-[#7ed321]/20 flex items-center justify-center group-hover:bg-[#7ed321] transition-colors">
                  <Phone className="w-5 h-5 text-[#6b2d7b] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="text-[#6b2d7b] font-medium">{contactData.phone}</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                <div className="w-12 h-12 rounded-lg bg-[#7ed321]/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#6b2d7b]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="text-[#6b2d7b] font-medium">Serving clients nationwide</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-slate-50 rounded-3xl p-8 lg:p-10">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#7ed321]/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-[#7ed321]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#6b2d7b] mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-slate-600">
                    Thank you for reaching out. We'll get back to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 font-medium">
                        Your Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        className="bg-white border-slate-200 focus:border-[#6b2d7b] focus:ring-[#6b2d7b] h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 font-medium">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        required
                        className="bg-white border-slate-200 focus:border-[#6b2d7b] focus:ring-[#6b2d7b] h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className="bg-white border-slate-200 focus:border-[#6b2d7b] focus:ring-[#6b2d7b] h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      How can we help? *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or needs..."
                      required
                      rows={5}
                      className="bg-white border-slate-200 focus:border-[#6b2d7b] focus:ring-[#6b2d7b] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#7ed321] hover:bg-[#6bc11a] text-[#2d1b38] font-semibold py-6 text-lg group"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-[#2d1b38]/30 border-t-[#2d1b38] rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Send Message
                        <Send className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
